import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
    MdTrendingUp,
    MdRefresh,
    MdAccountBalance,
    MdCheckCircle,
    MdHourglassEmpty,
    MdRedeem,
    MdAdd,
    MdListAlt,
    MdHistory,
    MdShowChart,
    MdGroups,
    MdChevronRight,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { getPageStyles, MOTION } from "../helpers/pageTheme";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const PAGE_LINKS = [
    {
        key: "create",
        title: "Create Recovery Trade",
        icon: MdAdd,
        path: "/create-recovery-trade",
        accent: "#059669",
    },
    {
        key: "list",
        title: "List of Recovery Trades",
        icon: MdListAlt,
        path: "/recovery-trade-list",
        accent: "#3b82f6",
    },
    {
        key: "history",
        title: "Commission History",
        icon: MdHistory,
        path: "/recovery-trade-commission-history",
        accent: "#8b5cf6",
    },
    {
        key: "chart",
        title: "Commission Chart",
        icon: MdShowChart,
        path: "/recovery-trade-commission-chart",
        accent: "#f59e0b",
    },
    {
        key: "team",
        title: "Recovery Team",
        icon: MdGroups,
        path: "/recovery-trade-team",
        accent: "#0ea5e9",
    },
];

const defaultStats = {
    invested: 0,
    claimed: 0,
    unclaimed: 0,
    pending: 0,
};

const formatCurrency = (amount) => {
    const n = Number(amount);
    if (isNaN(n)) return "$0.00";
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const StatTile = ({ icon: Icon, label, value, accent, isDark }) => (
    <div
        className="h-100 d-flex flex-column align-items-center justify-content-center text-center"
        style={{
            borderRadius: "12px",
            padding: "12px 10px",
            minHeight: "72px",
            backgroundColor: isDark ? "#0f172a" : "#ffffff",
            border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
            boxShadow: isDark
                ? "none"
                : "0 4px 14px rgba(15, 23, 42, 0.06)",
        }}
    >
        <Icon size={20} style={{ color: accent, marginBottom: "4px" }} />
        <span
            className="fw-bold text-truncate w-100"
            style={{ fontSize: "1rem", color: accent, lineHeight: 1.2 }}
        >
            {value}
        </span>
        <span
            className="text-uppercase text-truncate w-100"
            style={{
                fontSize: "0.68rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                color: isDark ? "#9ca3af" : "#6b7280",
                marginTop: "4px",
            }}
        >
            {label}
        </span>
    </div>
);

const PageLinkRow = ({ title, icon: Icon, accent, isDark, styles, isLast, onClick }) => (
    <motion.button
        type="button"
        className="btn w-100 d-flex align-items-center justify-content-between gap-2 border-0 text-start"
        style={{
            padding: "12px 16px",
            borderBottom: isLast ? "none" : `1px solid ${isDark ? "#1f2937" : "#f1f5f9"}`,
            borderRadius: 0,
            backgroundColor: "transparent",
            color: isDark ? "#f9fafb" : "#111827",
        }}
        whileHover={{ backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(99, 102, 241, 0.04)" }}
        whileTap={{ scale: 0.995 }}
        onClick={onClick}
    >
        <div className="d-flex align-items-center min-w-0">
            <span
                className="me-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "12px",
                    backgroundColor: isDark ? `${accent}33` : `${accent}18`,
                    color: accent,
                }}
            >
                <Icon size={22} />
            </span>
            <span
                className="fw-semibold text-truncate"
                style={{ fontSize: "0.875rem", color: isDark ? "#f9fafb" : "#111827" }}
            >
                {title}
            </span>
        </div>
        <MdChevronRight
            size={22}
            style={{ color: styles.textMuted, flexShrink: 0 }}
        />
    </motion.button>
);

const RecoveryTrade = ({ theme = "dark" }) => {
    const [stats, setStats] = useState(defaultStats);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const canClaim = stats.unclaimed > 0 && !loading && !claiming;

    const fetchStatistics = useCallback(
        async (isRefresh = false) => {
            const token = localStorage.getItem("a5srf4wer7w1cd");
            if (!token) {
                setError("Please login again");
                setLoading(false);
                return;
            }

            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            setError(null);

            try {
                const response = await axios.post(
                    `${BASE_API_URL}/recovery-trade/statistics`,
                    { token }
                );
                const data = response?.data;

                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }

                if (data?.error && data.error !== false) {
                    const msg =
                        typeof data.error === "string"
                            ? data.error
                            : data?.message || "Failed to load statistics";
                    setError(msg);
                    toast.error(msg);
                    return;
                }

                if (data?.data) {
                    setStats({
                        invested: Number(data.data.invested) || 0,
                        claimed: Number(data.data.claimed) || 0,
                        unclaimed: Number(data.data.unclaimed) || 0,
                        pending: Number(data.data.pending) || 0,
                    });
                }
            } catch (err) {
                const msg =
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    "Failed to load recovery trade statistics";
                setError(typeof msg === "string" ? msg : "Network error");
                toast.error("Failed to load statistics");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [navigate]
    );

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const handleRefresh = () => fetchStatistics(true);

    const handleClaimCommission = async () => {
        if (!canClaim) return;

        const token = localStorage.getItem("a5srf4wer7w1cd");
        if (!token) {
            navigate("/login");
            return;
        }

        setClaiming(true);
        try {
            const response = await axios.post(
                `${BASE_API_URL}/recovery-trade/claim`,
                { token }
            );
            const payload = response?.data;
            const apiError = getApiError(payload);

            if (apiError === "session expired") {
                localStorage.removeItem("a5srf4wer7w1cd");
                navigate("/login");
                return;
            }

            if (apiError) {
                toast.error(apiError);
                return;
            }

            const claimedAmount = Number(payload?.amount);
            const successMsg =
                payload?.msg || "Recovery trade commission claimed successfully";
            toast.success(
                !isNaN(claimedAmount) && claimedAmount > 0
                    ? `${successMsg} (${formatCurrency(claimedAmount)})`
                    : successMsg
            );

            await fetchStatistics(true);

            try {
                const authRes = await axios.post(
                    `${BASE_API_URL}/auth/check-token`,
                    { token }
                );
                const authData = authRes?.data;
                if (
                    !authData?.error &&
                    (authData?.balance != null || authData?.usdt != null)
                ) {
                    TriggerBalanceUpdate({
                        balance: authData.balance,
                        usdt: authData.usdt,
                    });
                }
            } catch {
                // Optional: refresh header balance after claim
            }
        } catch (err) {
            const payload = err.response?.data;
            if (payload) {
                const apiError = getApiError(payload);
                if (apiError === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                if (apiError) {
                    toast.error(apiError);
                    return;
                }
            }
            toast.error("Failed to claim recovery commission");
        } finally {
            setClaiming(false);
        }
    };

    const statItems = [
        {
            key: "invested",
            label: "Invested",
            value: stats.invested,
            icon: MdAccountBalance,
            accent: "#6366f1",
        },
        {
            key: "claimed",
            label: "Claimed",
            value: stats.claimed,
            icon: MdCheckCircle,
            accent: "#22c55e",
        },
        {
            key: "pending",
            label: "Pending",
            value: stats.pending,
            icon: MdHourglassEmpty,
            accent: "#f59e0b",
        },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="h-100 overflow-auto px-3 py-3"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                style={{ maxWidth: 480, margin: "0 auto" }}
            >
                <motion.div
                    variants={MOTION.item}
                    className="mb-3 p-4"
                    style={styles.hero(HERO_GRADIENT)}
                >
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <MdTrendingUp size={24} className="text-white flex-shrink-0" />
                            <div>
                                <h1 className="h5 fw-bold mb-0 text-white">
                                    Recovery Trade
                                </h1>
                                <small
                                    className="d-block"
                                    style={{
                                        color: "rgba(255,255,255,0.85)",
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    Commission overview
                                </small>
                            </div>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-flex align-items-center justify-content-center flex-shrink-0 border-0"
                            style={{
                                width: "40px",
                                height: "40px",
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.25)",
                                color: "#fff",
                                borderRadius: "10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleRefresh}
                            disabled={loading || refreshing || claiming}
                            aria-label="Refresh"
                        >
                            <motion.span
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{
                                    duration: 0.8,
                                    repeat: refreshing ? Infinity : 0,
                                    ease: "linear",
                                }}
                            >
                                <MdRefresh size={20} />
                            </motion.span>
                        </motion.button>
                    </div>

                    <div
                        className="text-center rounded-3 py-3 px-3"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                        }}
                    >
                        {loading ? (
                            <div
                                className="mx-auto"
                                style={{
                                    width: "80px",
                                    height: "24px",
                                    opacity: 0.4,
                                    backgroundColor: "rgba(255,255,255,0.3)",
                                    borderRadius: "8px",
                                }}
                            />
                        ) : (
                            <span
                                className="d-block fw-bold text-white"
                                style={{
                                    fontSize: "1rem",
                                    lineHeight: 1.2,
                                    textShadow: "0 1px 3px rgba(0,0,0,0.25)",
                                }}
                            >
                                {formatCurrency(stats.unclaimed)}
                            </span>
                        )}
                        <span
                            className="d-block text-uppercase"
                            style={{
                                color: "rgba(255,255,255,0.9)",
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                letterSpacing: "0.04em",
                                marginTop: "4px",
                            }}
                        >
                            Unclaimed commission
                        </span>
                        <motion.button
                            type="button"
                            className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-1 border-0 mt-3"
                            style={{
                                borderRadius: "10px",
                                padding: "8px 14px",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                background: canClaim
                                    ? "#ffffff"
                                    : "rgba(255,255,255,0.25)",
                                color: canClaim ? "#047857" : "rgba(255,255,255,0.55)",
                                cursor: canClaim ? "pointer" : "not-allowed",
                            }}
                            whileTap={canClaim ? { scale: 0.98 } : {}}
                            disabled={!canClaim}
                            onClick={handleClaimCommission}
                        >
                            {claiming ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                    />
                                    Claiming…
                                </>
                            ) : (
                                <>
                                    <MdRedeem size={18} />
                                    Claim commission
                                </>
                            )}
                        </motion.button>
                    </div>
                </motion.div>

                {error && !loading && (
                    <motion.div variants={MOTION.item} className="mb-3">
                        <div className="p-3 text-center" style={styles.card}>
                            <p className="mb-3" style={{ color: styles.textMuted, fontSize: "0.875rem" }}>
                                {error}
                            </p>
                            <motion.button
                                type="button"
                                className="btn"
                                style={{
                                    ...styles.submitBtn,
                                    background:
                                        "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                    fontSize: "0.875rem",
                                }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => fetchStatistics()}
                            >
                                Try again
                            </motion.button>
                        </div>
                    </motion.div>
                )}

                <motion.div variants={MOTION.item} className="mb-3">
                    <p className="mb-2 px-1" style={styles.sectionTitle}>
                        Statistics
                    </p>
                    {loading ? (
                        <div className="text-center py-4">
                            <div
                                className="spinner-border text-success mb-2"
                                role="status"
                            />
                            <p className="mb-0 small" style={{ color: styles.textMuted }}>
                                Loading statistics…
                            </p>
                        </div>
                    ) : (
                        <div className="row g-2">
                            {statItems.map(({ key, label, value, icon, accent }) => (
                                <div key={key} className="col-4">
                                    <StatTile
                                        icon={icon}
                                        label={label}
                                        value={formatCurrency(value)}
                                        accent={accent}
                                        isDark={isDark}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>

                <motion.div variants={MOTION.item} className="mb-3">
                    <div style={styles.card}>
                        {PAGE_LINKS.map(({ key, title, icon, path, accent }, index) => (
                            <PageLinkRow
                                key={key}
                                title={title}
                                icon={icon}
                                accent={accent}
                                isDark={isDark}
                                styles={styles}
                                isLast={index === PAGE_LINKS.length - 1}
                                onClick={() => navigate(path)}
                            />
                        ))}
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default RecoveryTrade;
